import { t as translate } from 'i18n';
import { formatTick } from './units';

/**
 * What every chart with a time or category axis shares with the trend cards:
 * the selected tick, the callout over a point and the reference lines. Each is
 * a plain piece of an ECharts option, so a hover redraws one small patch.
 * `view` is live, `{selected, width}`: the axis reads it while it lays out.
 */
export const BAR_RADIUS = 4;
const CALLOUT_PADDING = 8;
const ARROW = { width: 8, height: 5 };
const NARROW_SLOT = 34;
// A chart measured before its first layout still needs sensible ticks.
const ASSUMED_WIDTH = 640;
// Named categories are the chart's meaning: up to this many keep every label.
const WRAPPED_CATEGORIES = 12;
const LABEL_GAP = 8;

let context;

function textWidth(text, font) {
  if (typeof document === 'undefined') return text.length * 7;
  context ||= document.createElement('canvas').getContext('2d');
  if (!context) return text.length * 7;
  context.font = font;
  return context.measureText(text).width;
}

/** Tick text reaches a rich-text parser, where braces would be read as markup. */
const plainTick = (tick) => String(tick).replace(/[{}]/g, '');

/** Ticks thin out so they never collide; the selected one replaces its own label. */
export function selectedTicks({ ticks, categories, view, theme, accent, left, right, wrap = false }) {
  const { fontFamily, type, inkMuted } = theme;
  const labels = ticks.map(plainTick);
  // The engine numbers a formatter's ticks from the zoom window, an interval's
  // from the data: unique categories name their own place in either.
  const places = categories ? new Map(categories.map((category, index) => [category, index])) : null;
  let measured = null;
  const spacing = () => {
    const width = view.width || ASSUMED_WIDTH;
    const visible = view.visible || labels.length;
    if (measured?.width !== width || measured.visible !== visible) {
      const slot = (width - left - right) / visible;
      const narrow = slot < NARROW_SLOT;
      const widest = Math.max(...labels.map((label) => textWidth(label, `500 ${narrow ? type.axis - 1 : type.axis}px ${fontFamily}`)));
      const font = `500 ${narrow ? type.axis - 1 : type.axis}px ${fontFamily}`;
      const longestWord = Math.max(...labels.flatMap((label) => label.split(/\s+/).map((word) => textWidth(word, font))));
      // Wrapping must not cut words: a slot too narrow for one thins out instead.
      const wrapped = wrap && visible <= WRAPPED_CATEGORIES && slot - LABEL_GAP >= longestWord;
      const every = slot > 0 ? Math.max(1, Math.ceil((widest + (narrow ? 12 : 20)) / slot)) : labels.length;
      const lines = wrapped ? Math.max(1, Math.ceil(widest / (slot - LABEL_GAP))) : 1;
      measured = { width, visible, narrow, every: wrapped ? 1 : every, labelWidth: wrapped ? slot - LABEL_GAP : undefined, lines };
    }
    return measured;
  };
  const size = spacing().narrow ? type.axis - 1 : type.axis;
  view.tickLines = spacing().lines;
  view.tickLine = size + 3;
  view.firstTickLines ??= view.tickLines;
  return {
    interval: (index) => {
      if (index === view.selected) return true;
      const { every } = spacing();
      return index % every === 0 && (view.selected < 0 || Math.abs(index - view.selected) >= every);
    },
    formatter: (category, position) => {
      const index = places?.get(category) ?? position;
      return index === view.selected ? `{selected|${labels[index]}}` : labels[index];
    },
    margin: 12, hideOverlap: false, fontFamily, fontSize: size, color: inkMuted,
    ...(spacing().labelWidth ? { width: spacing().labelWidth, overflow: 'break', lineHeight: size + 3 } : {}),
    rich: { selected: { backgroundColor: accent.pill, color: accent.pillText, fontWeight: 600, fontFamily, fontSize: size, borderRadius: 10, padding: spacing().narrow ? [3, 5] : [4, 9] } },
  };
}

/** The callout over a point, kept inside the canvas while its arrow stays on the point. */
export function calloutMark({ theme, accent, index, count, value, at = value, unit, view, left, right }) {
  const known = value !== null && value !== undefined;
  const below = known && value < 0;
  const text = known ? formatTick(value, unit) : translate('assistant.answerWidgetNoData');
  const font = `600 ${theme.type.label}px ${theme.fontFamily}`;
  const half = textWidth(text, font) / 2 + CALLOUT_PADDING;
  const width = view.width || ASSUMED_WIDTH;
  const centre = left + ((index - (view.first || 0) + 0.5) * (width - left - right)) / Math.max(1, view.visible || count);
  const shift = Math.max(2 + half - centre, Math.min(0, width - 2 - half - centre));
  return {
    silent: true, animation: false, z: 10, symbol: 'triangle', symbolRotate: below ? 0 : 180, symbolSize: [ARROW.width, ARROW.height],
    symbolOffset: [0, below ? 6 : -6], itemStyle: { color: accent.colour, borderWidth: 0 }, data: [{ coord: [index, known ? at : 0] }],
    label: {
      show: true, position: below ? 'bottom' : 'top', distance: 0, offset: [shift, 0], formatter: text, fontFamily: theme.fontFamily, fontSize: theme.type.label, fontWeight: 600,
      lineHeight: theme.type.label + 2, color: accent.on, backgroundColor: accent.colour, borderRadius: 6, padding: [4, CALLOUT_PADDING],
    },
  };
}

/** A zero baseline and tagged lines: `[{value, label?, colour?, dashed?}]`. */
export function referenceLines(theme, lines, axis = 'yAxis') {
  const { type, fontFamily, chart } = theme;
  return {
    silent: true, symbol: 'none', animation: false, label: { show: false },
    data: lines.map(({ value, label, colour, dashed = true }) => ({
      [axis]: value,
      lineStyle: { color: colour || chart?.neutral?.average || theme.inkMuted, type: dashed ? [2, 3] : 'solid', width: 1 },
      label: label ? {
        show: true, position: axis === 'xAxis' ? 'end' : 'insideStartTop', rotate: 0, distance: axis === 'xAxis' ? 4 : 0, formatter: plainTick(label).replace(/\|/g, ' '), fontFamily, fontSize: type.axis - 1, fontWeight: 500,
        lineHeight: type.axis + 3, color: chart?.neutral?.tagOn || theme.surface, backgroundColor: chart?.neutral?.tag || theme.ink, borderRadius: 4, padding: [2, 6],
      } : { show: false },
    })),
  };
}

/** The accent of a `tone`, falling back to the theme's first one. */
export const toneOf = (tones, name) => tones?.[name] || tones?.teal;
